import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  TablePagination,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { assessmentService } from '@/services/assessment/assessmentService';
import type { AttemptListItem } from '@/types/assessment';

const AttemptsTab: React.FC = () => {
  const [attempts, setAttempts] = useState<AttemptListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const navigate = useNavigate();

  const fetchAttempts = async (page: number = 1, limit: number = 5) => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getMyAttempts({
        page,
        limit,
      });

      if (response.success && response.data) {
        setAttempts(response.data.attempts);
        setCurrentPage(response.data.pagination.currentPage);
        setTotalAttempts(response.data.pagination.totalAttempts);
      } else {
        setError('Failed to fetch attempts');
      }
    } catch (err) {
      setError('Error loading attempts');
      console.error('Error fetching attempts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setCurrentPage(newPage + 1); // TablePagination uses 0-based indexing, but our API uses 1-based
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handleViewDetails = (attemptId: string) => {
    navigate(`/attempts/${attemptId}`);
  };

  const handleViewAll = () => {
    navigate('/attempts');
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'abandoned':
        return 'error';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'in_progress':
        return <ScheduleIcon />;
      case 'abandoned':
      case 'expired':
        return <CancelIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Recent Attempts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalAttempts > 0 
              ? `Total: ${totalAttempts} attempt${totalAttempts !== 1 ? 's' : ''}`
              : 'No attempts yet'
            }
          </Typography>
        </Box>
        {totalAttempts > 0 && (
          <Button variant="outlined" onClick={handleViewAll}>
            View All Attempts
          </Button>
        )}
      </Box>

      {attempts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No attempts found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You haven't taken any assessments yet. Start with your first assessment!
            </Typography>
            {/* <Button variant="contained" onClick={() => navigate('/assessment')}>
              Take Assessment
            </Button> */}
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {attempts.map((attempt) => (
              <Grid size={{xs: 12, md: 6}} key={attempt._id}>
                <Card sx={{ '&:hover': { boxShadow: 2 }, height: '100%' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box flex={1}>
                        <Typography variant="subtitle1" gutterBottom noWrap>
                          {attempt.assessment.title}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={attempt.assessment.difficulty}
                            color={attempt.assessment.difficulty === 'Beginner' ? 'success' : 
                                   attempt.assessment.difficulty === 'Intermediate' ? 'warning' : 'error'}
                          />
                          <Chip
                            size="small"
                            icon={getStatusIcon(attempt.status)}
                            label={attempt.status.replace('_', ' ').toUpperCase()}
                            color={getStatusColor(attempt.status)}
                          />
                        </Stack>
                      </Box>
                    </Box>

                    <Box mb={2}>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Box sx={{ width: '100%' }}>
                          <LinearProgress
                            variant="determinate"
                            value={attempt.score.percentage}
                            sx={{ height: 6, borderRadius: 3 }}
                            color={attempt.score.isPassed ? 'success' : 'error'}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ minWidth: '50px' }}>
                          {attempt.score.percentage}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {attempt.score.correctAnswers}/{attempt.score.totalQuestions} correct
                        {' • '}
                        {formatDuration(attempt.timing.totalTimeSpent)}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            bgcolor: attempt.score.isPassed ? 'success.main' : 'error.main',
                            fontSize: '0.75rem',
                          }}
                        >
                          {attempt.score.isPassed ? (
                            <CheckCircleIcon sx={{ fontSize: 12 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 12 }} />
                          )}
                        </Avatar>
                        <Typography variant="caption">
                          {attempt.score.isPassed ? 'PASSED' : 'FAILED'}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(attempt._id)}
                      >
                        View
                      </Button>
                    </Box>

                    {attempt.timing.completedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Completed: {formatDate(attempt.timing.completedAt)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalAttempts > rowsPerPage && (
            <TablePagination
              component="div"
              count={totalAttempts}
              page={currentPage - 1} // Convert to 0-based indexing for TablePagination
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 15, 25, 50]}
              labelRowsPerPage="Items per page:"
              showFirstButton
              showLastButton
              sx={{ mt: 2, borderTop: '1px solid', borderTopColor: 'divider' }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default AttemptsTab;